import {
  AgaveAccount,
  AgaveAPInvoicesCreateExpenseInput,
  AgaveAPInvoicesLineItemInput,
  CreateAgaveExpenseAPInvoice,
  CreateAgaveExpenseAPInvoiceLineItem,
  CreateAgaveExpenseInput,
  CreateAgaveExpenseLineItemInput,
  CreateAgaveExpensePaymentMethodInput,
  HighnoteTransactionEvent,
} from 'API';
import { formatAmountForAgavePost } from 'app/services/agave/erpUtils';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';

export type QBOCreditCardCreditPassthroughArgs = {
  account_token: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
};

export const qboUtil = {
  speedchainMerchantName: 'SPEEDCHAIN',
  agaveSourceSystem: AgaveSourceSystem.QUICKBOOKS_ONLINE,

  buildExpenseFields: (txn: HighnoteTransactionEvent, agaveAccount: AgaveAccount, overrides = {}) => {
    const { amount, amountValue } = formatAmountForAgavePost(txn.approvedAmount?.value ?? 0, txn.status);
    return {
      amountStr: amount,
      amountValue,
      description: (txn.merchantName || '') + (txn.note ? ' - ' + txn.note : ''),
      erp: agaveAccount.source_system?.slug || '',
      // use snake case to allow unpacking into correct payload fields
      payment_date: new Date(txn.eventDate ?? txn.createdAt).toISOString().split('T')[0],
      payment_number: txn.id.substring(0, 10),
      // values from the coding
      ...(txn.agaveCostCode?.source_id && {
        cost_code_sid: `source_id:${txn.agaveCostCode.source_id}`,
      }),
      ...(txn.agaveProject?.source_id && { customer_id: `source_id:${txn.agaveProject.source_id}` }),
      ...(txn.agaveGlCodeId && { ledger_account_id: txn.agaveGlCodeId }),
      ...(txn.agaveVendorId && { vendor_id: txn.agaveVendorId }),
      ...overrides,
    };
  },

  buildExpenseForCreateExpense: (
    transaction: HighnoteTransactionEvent,
    agaveAccount: AgaveAccount,
    agaveAccountToken: string
  ): CreateAgaveExpenseInput => {
    const {
      amountStr,
      cost_code_sid,
      customer_id,
      description,
      erp,
      ledger_account_id,
      payment_date,
      payment_number,
      vendor_id,
    } = qboUtil.buildExpenseFields(transaction, agaveAccount);

    const line_items: CreateAgaveExpenseLineItemInput[] = [
      {
        amount: amountStr,
        description,
        quantity: '1.0',
        status: 'NotBillable',
        unit_cost: amountStr,
        ...(ledger_account_id && { ledger_account_id }),
        ...(customer_id && { customer_id }),
        // Use type field for cost type (maps to CLASS column)
        ...(transaction.agaveCostType?.name && { type: transaction.agaveCostType?.name }),
        // handle case with cost code but no gl code
        ...(cost_code_sid && !ledger_account_id && { item_id: cost_code_sid }),
      },
    ];

    // then if we have both a cost code and a gl code:
    if (transaction.agaveCostCodeId && transaction.agaveGlCodeId) {
      line_items.push({
        amount: '0.00',
        description: '',
        item_id: cost_code_sid,
        quantity: '1.0',
        status: 'NotBillable',
        unit_cost: '0.00',
      });
    }

    const expense: CreateAgaveExpenseInput = {
      account_token: agaveAccountToken,
      description,
      erp,
      line_items,
      merchantName: qboUtil.speedchainMerchantName,
      number: payment_number,
      payment_date,
      payment_method: CreateAgaveExpensePaymentMethodInput.CreditCard,
      ...(vendor_id && { vendor_id }),
    };
    return expense;
  },

  buildAPInvoiceForCreateExpense: (
    transaction: HighnoteTransactionEvent,
    agaveAccount: AgaveAccount,
    agaveAccountToken: string
  ): AgaveAPInvoicesCreateExpenseInput => {
    const {
      amountStr,
      cost_code_sid,
      customer_id,
      description,
      erp,
      ledger_account_id,
      payment_date,
      payment_number,
      vendor_id,
    } = qboUtil.buildExpenseFields(transaction, agaveAccount);

    const line_items: AgaveAPInvoicesLineItemInput[] = [
      {
        amount: amountStr,
        description,
        quantity: '1.0',
        status: 'NotBillable',
        unit_cost: amountStr,
        ...(ledger_account_id && { ledger_account_id }),
        ...(customer_id && { customer_id }),
        ...(transaction.agaveCostTypeId && { cost_type_id: transaction.agaveCostTypeId }),
        // handle case with cost code but no gl code
        ...(cost_code_sid && !ledger_account_id && { item_id: cost_code_sid }),
      },
    ];

    if (transaction.agaveCostCodeId && transaction.agaveGlCodeId) {
      line_items.push({
        amount: '0.00',
        description: '',
        item_id: cost_code_sid,
        quantity: '1.0',
        status: 'NotBillable',
        unit_cost: '0.00',
      });
    }

    const expense: AgaveAPInvoicesCreateExpenseInput = {
      account_token: agaveAccountToken,
      description,
      erp,
      line_items,
      issue_date: payment_date,
      merchantName: qboUtil.speedchainMerchantName,
      number: payment_number,
      ...(vendor_id && { vendor_id }),
    };
    return expense;
  },

  /**
   * Builds the QuickBooks Online Purchase payload representing a credit (refund).
   * this ends up being passed to the agaveResolverMutation function which is resolved on the backend
   */
  buildRefundForAgaveResolver(
    transaction: HighnoteTransactionEvent,
    agaveAccount: AgaveAccount,
    agaveAccountToken: string
  ) {
    const { amountValue } = formatAmountForAgavePost(
      transaction.approvedAmount?.value ?? 0,
      transaction.status
    );
    // passthrough vals
    const description = `Credit Card Credit - ${transaction.merchantName || 'Transaction'} - Refund`;
    const note = transaction.note || '';
    const paymentNumber = transaction.id.substring(0, 10);
    const paymentDate = new Date(transaction.eventDate ?? transaction.createdAt).toISOString().split('T')[0];
    const amountStr = amountValue < 0 ? -1 * amountValue : amountValue;

    const passthroughData = {
      path: `/v3/company/${agaveAccount.linked_company?.source_id}/purchase`,
      method: 'POST',
      data: {
        PaymentType: 'CreditCard',
        Credit: true,
        DocNumber: paymentNumber,
        TxnDate: paymentDate,
        PrivateNote: note,
        // AccountRef - should come from the backend /bank-accounts endpoint
        // the EntityRef is the payee in the upper right of quickbooks
        ...(transaction.agaveVendorId &&
          transaction.agaveVendor?.source_id &&
          transaction.agaveVendor?.name && {
            EntityRef: {
              value: transaction.agaveVendor.source_id,
              name: transaction.agaveVendor.name,
              type: 'Vendor',
            },
          }),
        Line: [
          {
            DetailType: 'AccountBasedExpenseLineDetail',
            Description: description,
            Amount: amountStr,
            AccountBasedExpenseLineDetail: {
              BillableStatus: 'NotBillable',
              ...(transaction.agaveGlCodeId &&
                transaction.agaveGlCode?.source_id &&
                transaction.agaveGlCode?.name && {
                  AccountRef: {
                    value: transaction.agaveGlCode.source_id,
                    name: transaction.agaveGlCode.name,
                  },
                }),
              ...(transaction.agaveProjectId &&
                transaction.agaveProject?.source_id &&
                transaction.agaveProject?.name && {
                  CustomerRef: {
                    value: transaction.agaveProject.source_id,
                    name: transaction.agaveProject.name,
                  },
                }),
              ...(transaction.agaveCostTypeId &&
                transaction.agaveCostType?.source_id &&
                transaction.agaveCostType?.name && {
                  ClassRef: {
                    value: transaction.agaveCostType.source_id,
                    name: transaction.agaveCostType.name,
                  },
                }),
            },
          },
        ],
      },
    };

    const refundPassthrough = {
      handler: 'HandleRefund',
      erp: qboUtil.agaveSourceSystem,
      passthroughData,
      fromBackend: { 'data.AccountRef': { function: 'getBankAccountAccountRef' } },
      extra: {},
    };

    return {
      account_token: agaveAccountToken,
      data: JSON.stringify(refundPassthrough),
    };
  },

  getQuickbooksCompanyId: (agaveAccount: AgaveAccount) => {
    return agaveAccount.linked_company?.source_id ?? null;
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildCreateAgaveExpenseFromCreditCardCredit(creditCardCredit: any): CreateAgaveExpenseAPInvoice {
    // construct the object that we can use to create the expense.  Since the credit card credit object contains different fields, we must select the fields that will allow us to create the expense
    // otherwise, we will need to add/change the schema to allow for the credit card credit object to work with the `updateHighnoteTransaction` mutation.
    //
    // from comparing the objects, it seems like the fields on the credit card credit object that will cause
    // the graphql marshaller to throw an error are the following:
    // - bank_account_id
    // - ledger_account_id
    // - payment_date
    // - payment_method
    // - payment_number

    const expense: CreateAgaveExpenseAPInvoice = {
      __typename: 'CreateAgaveExpenseAPInvoice',
      id: creditCardCredit?.id,
      amount: creditCardCredit?.amount ?? null,
      amount_due: creditCardCredit?.amount_due ?? null,
      ap_payment_ids: creditCardCredit?.ap_payment_ids ?? null, // this is likely NOT on the credit card credit
      attachments: creditCardCredit?.attachments ?? null,
      batch: creditCardCredit?.batch ?? null,
      batch_id: creditCardCredit?.batch_id ?? null,
      contract: creditCardCredit?.contract ?? null,
      description: creditCardCredit?.description ?? null,
      number: creditCardCredit?.number ?? null,
      project_id: creditCardCredit?.project_id ?? null,
      source_id: creditCardCredit?.source_id ?? null,
      source_create_time: creditCardCredit?.source_create_time ?? null,
      source_update_time: creditCardCredit?.source_update_time ?? null,
      status: creditCardCredit?.status ?? null,
      total_amount: creditCardCredit?.total_amount ?? null,
      type: creditCardCredit?.type ?? null,
      vendor_id: creditCardCredit?.vendor_id ?? null,
      // line_items and source_data are possibly to be nested objects, so attach them last
      // remove the source_data from the line items that are returned on the credit card credit
      line_items: Array.isArray(creditCardCredit.line_items)
        ? creditCardCredit.line_items.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ({ source_data: _omit, ...rest }: any): CreateAgaveExpenseAPInvoiceLineItem | null =>
              rest as CreateAgaveExpenseAPInvoiceLineItem
          )
        : null,
      source_data: creditCardCredit?.source_data ?? null,
    };

    return expense;
  },
};

export default qboUtil;
